import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { CONDUCTOR_THEMES } from './theme.js';
import type { Appearance, Density, ThemeDirection } from './types.js';

const THEME_ROWS: { direction: ThemeDirection; name: string }[] = [
  { direction: 'signal', name: 'Signal' },
  { direction: 'podium', name: 'Podium' },
  { direction: 'foundry', name: 'Foundry' },
];

const DENSITY_ROWS: { density: Density; name: string }[] = [
  { density: 'regular', name: 'Regular' },
  { density: 'compact', name: 'Compact' },
];

function GearIcon() {
  return (
    <svg width={16} height={16} viewBox="0 0 16 16" fill="none" aria-hidden>
      <circle cx={8} cy={8} r={2.25} stroke="currentColor" strokeWidth={1.4} />
      <path
        d="M8 1.5v1.6M8 12.9v1.6M14.5 8h-1.6M3.1 8H1.5M12.4 3.6l-1.13 1.13M4.73 11.27L3.6 12.4M12.4 12.4l-1.13-1.13M4.73 4.73L3.6 3.6"
        stroke="currentColor"
        strokeWidth={1.4}
        strokeLinecap="round"
      />
    </svg>
  );
}

function ThemeSwatch({ direction }: { direction: ThemeDirection }) {
  const tokens = CONDUCTOR_THEMES[direction].tokens;
  return (
    <span style={{ position: 'relative', width: 22, height: 14, flexShrink: 0 }}>
      <span
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: 14,
          height: 14,
          borderRadius: '50%',
          background: tokens['--bg'],
          border: '1px solid var(--border2)',
        }}
      />
      <span
        style={{
          position: 'absolute',
          left: 8,
          top: 0,
          width: 14,
          height: 14,
          borderRadius: '50%',
          background: tokens['--accent'],
          border: '1px solid var(--border2)',
        }}
      />
    </span>
  );
}

export default function AppearanceMenu({
  appearance,
  setAppearance,
}: {
  appearance: Appearance;
  setAppearance: (a: Appearance) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onMouseDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Appearance"
        style={{
          width: 38,
          height: 38,
          background: 'transparent',
          border: '1px solid var(--border2)',
          borderRadius: 'var(--btn-radius)',
          color: 'var(--muted)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
      >
        <GearIcon />
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            width: 240,
            background: 'var(--surface)',
            border: '1px solid var(--border2)',
            borderRadius: 'var(--radius)',
            boxShadow: '0 12px 32px rgba(0,0,0,.25)',
            padding: 12,
            zIndex: 20,
          }}
        >
          <div style={sectionLabelStyle}>Theme</div>
          {THEME_ROWS.map((row) => (
            <button
              key={row.direction}
              type="button"
              onClick={() => setAppearance({ ...appearance, direction: row.direction })}
              style={menuRowStyle}
            >
              <ThemeSwatch direction={row.direction} />
              <span style={{ flex: 1, textAlign: 'left', color: 'var(--text)', fontSize: 13 }}>
                {row.name}
              </span>
              {appearance.direction === row.direction && <CheckIcon />}
            </button>
          ))}

          <div style={{ ...sectionLabelStyle, marginTop: 12 }}>Density</div>
          {DENSITY_ROWS.map((row) => (
            <button
              key={row.density}
              type="button"
              onClick={() => setAppearance({ ...appearance, density: row.density })}
              style={menuRowStyle}
            >
              <span style={{ flex: 1, textAlign: 'left', color: 'var(--text)', fontSize: 13 }}>
                {row.name}
              </span>
              {appearance.density === row.density && <CheckIcon />}
            </button>
          ))}

          <div style={{ ...sectionLabelStyle, marginTop: 12 }}>Preview</div>
          <button
            type="button"
            onClick={() =>
              setAppearance({ ...appearance, communityPreview: !appearance.communityPreview })
            }
            style={menuRowStyle}
          >
            <span style={{ flex: 1, textAlign: 'left', color: 'var(--text)', fontSize: 13 }}>
              Community tab
            </span>
            {appearance.communityPreview && <CheckIcon />}
          </button>

          <div
            style={{
              marginTop: 12,
              paddingTop: 10,
              borderTop: '1px solid var(--border)',
              fontSize: 10,
              color: 'var(--faint)',
            }}
          >
            Saved on this device.
          </div>
        </div>
      )}
    </div>
  );
}

const sectionLabelStyle: CSSProperties = {
  fontSize: 10,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'var(--faint)',
  fontWeight: 600,
  padding: '4px 6px',
};

const menuRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  padding: '6px',
  background: 'transparent',
  border: 'none',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
};

function CheckIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 14 14" fill="none" aria-hidden>
      <path
        d="M2.5 7.3 5.5 10.3 11.5 3.8"
        stroke="var(--accent)"
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
